import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  IconButton,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  VerifiedUser as VerifiedUserIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import AdminApiModule from "./Api/api";
import { BASE_URL } from "../config/config";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

// Removed PUNCH_TICKET_API - using updateTicketVerificationStatus API instead

const TicketDocument = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingSerial, setIsVerifyingSerial] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // State to hold the ticket data
  const [stateData, setStateData] = useState(() => {
    // Initialize from location.state if available
    if (location.state && Object.keys(location.state).length > 0) {
      // Also save to sessionStorage for refresh persistence
      try {
        sessionStorage.setItem(
          "ticketDocumentState",
          JSON.stringify(location.state)
        );
        console.log("TicketDocView - Saved location.state to sessionStorage");
      } catch (error) {
        console.error("Error saving to sessionStorage:", error);
      }
      return location.state;
    }
    try {
      const storedState = sessionStorage.getItem("ticketDocumentState");
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        return parsedState;
      }
    } catch (error) {
      console.error("Error reading from sessionStorage on init:", error);
    }
    return null;
  });

  // Normalize imageUrls - handle different formats
  const normalizeImageUrls = (urls) => {
    if (!urls) return [];
    if (Array.isArray(urls)) {
      // If it's an array, filter out null/undefined and ensure strings
      return urls.filter((url) => url && typeof url === "string");
    }
    // If it's an object, try to get onsite_pictures
    if (urls.onsite_pictures && Array.isArray(urls.onsite_pictures)) {
      return urls.onsite_pictures.filter(
        (url) => url && typeof url === "string"
      );
    }
    return [];
  };

  // Load state from sessionStorage on mount (for new tab scenario and page refresh)
  useEffect(() => {
    // If we don't have stateData, try to load from sessionStorage
    // This handles both new tab scenario and page refresh
    if (!stateData) {
      try {
        const storedState = sessionStorage.getItem("ticketDocumentState");
        if (storedState) {
          const parsedState = JSON.parse(storedState);
          console.log(
            "TicketDocView - Loaded from sessionStorage:",
            parsedState
          );
          setStateData(parsedState);
          // Don't clear sessionStorage - keep it for page refresh persistence
        }
      } catch (error) {
        console.error("Error reading from sessionStorage:", error);
      }
    }
    // If we have stateData from location.state, also save it to sessionStorage for refresh persistence
    else if (
      stateData &&
      location.state &&
      Object.keys(location.state).length > 0
    ) {
      try {
        sessionStorage.setItem(
          "ticketDocumentState",
          JSON.stringify(stateData)
        );
        console.log(
          "TicketDocView - Saved stateData to sessionStorage for refresh persistence"
        );
      } catch (error) {
        console.error("Error saving to sessionStorage:", error);
      }
    }
  }, []); // Run only once on mount

  // Note: We intentionally keep sessionStorage data for page refresh persistence
  // It will be cleared automatically when the browser tab/window is closed
  // or when a new ticket document is opened (which will overwrite it)

  // Get imageUrls from state, handle both array and object formats
  const stateImageUrls = stateData?.imageUrls || [];
  const imageUrls = normalizeImageUrls(stateImageUrls);

  // Get ticketId from state first, then fallback to localStorage
  const stateTicketId = stateData?.ticketId;
  let ticketId = stateTicketId || localStorage.getItem("ticketId");

  // Get verification info and ticket data from state
  const ticketVerification = stateData?.ticketVerification || null;
  const ticketData = stateData?.ticketData || null;
  const isVerified =
    stateData?.isVerified ||
    ticketVerification?.verification_status === "VERIFIED";

  // Set ticketId in localStorage if we got it from state
  useEffect(() => {
    if (stateTicketId) {
      localStorage.setItem("ticketId", stateTicketId);
    }
  }, [stateTicketId]);

  const [verificationStatus, setVerificationStatus] = useState(null);
  const [duplicateTickets, setDuplicateTickets] = useState([]);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isAutoVerified, setIsAutoVerified] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [serialVerificationStatus, setSerialVerificationStatus] =
    useState(null); // 'verified', 'duplicate', 'mismatch', null
  const [serialMismatchData, setSerialMismatchData] = useState(null); // Store mismatch data from API
  const [apiMessage, setApiMessage] = useState(null); // Store API message
  const globalState = useSelector((state) => state.AdminReducer);

  // Debug: Log imageUrls to see what we're receiving
  useEffect(() => {
    const stored = sessionStorage.getItem("ticketDocumentState");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
      } catch (e) {
        console.error("Error parsing sessionStorage:", e);
      }
    }
  }, [stateData, stateImageUrls, imageUrls, ticketId, location.state]);

  const adminApi = new AdminApiModule();

  // Initialize values - pre-populate if verified
  const getInitialValues = () => {
    if (isVerified && ticketVerification) {
      return {
        serialNumber: ticketData?.serial_number || "",
        remark:
          ticketVerification.remarks ||
          ticketVerification.verification_message ||
          "",
        ticket_status: ticketVerification.verification_status || "VERIFIED",
      };
    }
    return {
      serialNumber: "",
      remark: "",
      ticket_status: "",
    };
  };

  const initialValues = getInitialValues();

  // Validation schema using Yup - dynamic based on state
  const getValidationSchema = () => {
    return Yup.object({
      serialNumber: Yup.string().test(
        "serial-required",
        "Serial Number is required",
        function (value) {
          const { ticket_status } = this.parent;
          // Serial number is required if status is not ON_HOLD or REJECT
          if (
            ticket_status &&
            ticket_status !== "ON_HOLD" &&
            ticket_status !== "REJECT"
          ) {
            return value && value.trim().length > 0;
          }
          return true;
        }
      ),
      remark: Yup.string().test(
        "remark-required",
        "Remarks are required",
        function (value) {
          const { ticket_status } = this.parent;
          // Remarks are required for: REJECT, ON_HOLD, or when duplicate is found
          if (ticket_status === "ON_HOLD" || ticket_status === "REJECT") {
            return value && value.trim().length > 0;
          }
          // If duplicate is found, remarks are required (handled in validateForm)
          return true;
        }
      ),
      ticket_status: Yup.string().required(
        "Document verification status is required"
      ),
    });
  };

  // Custom validation function to handle duplicate requirement
  const validateForm = (values) => {
    const errors = {};

    // If duplicate is found, remarks are required
    if (isDuplicate && !values.remark?.trim()) {
      errors.remark =
        "Remarks are required when duplicate serial number is found";
    }

    return errors;
  };

  // Verify serial number for duplicates
  const handleVerifySerialNumber = async () => {
    if (!formik.values.serialNumber.trim()) {
      toast.error("Please enter a serial number first");
      return;
    }

    // Validate ticketId - should not be empty or invalid values like "PUBLIC_WITH_CLIENT_QR"
    if (
      !ticketId ||
      ticketId.trim() === "" ||
      ticketId === "PUBLIC_WITH_CLIENT_QR" ||
      ticketId.length < 10
    ) {
      toast.error("Invalid ticket ID. Please go back and try again.");
      console.error("TicketDocView - Invalid ticketId:", ticketId);
      return;
    }

    setIsVerifyingSerial(true);
    setSerialVerificationStatus(null);
    setIsDuplicate(false);
    setIsAutoVerified(false);
    setDuplicateTickets([]);
    setSerialMismatchData(null);
    setApiMessage(null);

    try {
      console.log("TicketDocView - Verifying serial with ticketId:", ticketId);
      const response = await adminApi.checkDuplicateSerial({
        ticket_obj_id: ticketId,
        serial_number: formik.values.serialNumber,
      });

      // Check for mismatch (serial number mismatch)
      if (response.data && response.data.mismatch === true) {
        // Serial number mismatch - show popup with mismatch data
        setIsDuplicate(true);
        setSerialVerificationStatus("mismatch");
        setSerialMismatchData(response.data.data || {});
        setApiMessage(response.data.message || "Serial number mismatch");
        // Show confirmation dialog
        setConfirmDialogOpen(true);
        // Don't show toast here - message will be shown in dialog
      } else if (response.data && response.data.is_duplicate) {
        // Duplicate found - fetch list of tickets with this serial number
        setIsDuplicate(true);
        setSerialVerificationStatus("duplicate");
        await fetchDuplicateTickets(formik.values.serialNumber);
        setConfirmDialogOpen(true);
        toast.warning(
          response.data.message ||
            "Serial number already exists. Please review the duplicate tickets."
        );
      } else {
        // No duplicate - auto verify
        setIsDuplicate(false);
        setIsAutoVerified(true);
        setSerialVerificationStatus("verified");
        // Auto-set ticket status to VERIFIED
        formik.setFieldValue("ticket_status", "VERIFIED");
        toast.success(
          response.data?.message ||
            "Serial number verified successfully! Ticket will be auto-verified."
        );
      }
    } catch (error) {
      console.error("Error checking duplicate serial:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error verifying serial number. Please try again.";
      toast.error(errorMessage);
      setSerialVerificationStatus(null);
    } finally {
      setIsVerifyingSerial(false);
    }
  };

  // Fetch tickets with duplicate serial number
  const fetchDuplicateTickets = async (serialNumber) => {
    try {
      // Only send serial_number in the payload
      const params = {
        serial_number: serialNumber,
      };

      const response = await adminApi.getTicketsBySerialNumber(params);

      // Show API message if available
      if (response.data?.message) {
        toast.info(response.data.message);
      }

      // Set duplicate tickets from response
      if (response.data && response.data.data) {
        // Handle both 'data' array and 'results' array formats
        const tickets = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.results || [];
        setDuplicateTickets(tickets);
      }
    } catch (error) {
      console.error("Error fetching duplicate tickets:", error);
      const errorMessage =
        error.response?.data?.message || "Error fetching duplicate tickets";
      toast.error(errorMessage);
    }
  };

  const onSubmit = async (values) => {
    // If REJECT or ON_HOLD, use different API
    if (
      values.ticket_status === "REJECT" ||
      values.ticket_status === "ON_HOLD"
    ) {
      await handleRejectOrOnHold(values);
      return;
    }

    // If duplicate/mismatch found and dialog is still open, don't proceed
    // User needs to click Ok first to close dialog, then can submit
    if (isDuplicate && !isAutoVerified && confirmDialogOpen) {
      return;
    }

    // Auto-verified or user clicked Ok on duplicate dialog - proceed with submission
    await handleSubmitTicket(values);
  };

  const handleRejectOrOnHold = async (values) => {
    setIsLoading(true);
    try {
      const data = {
        ticket_obj_id: ticketId,
        verification_status: values.ticket_status,
        verification_message: values.remark,
      };

      const response = await adminApi.updateTicketVerificationStatus(data);

      if (response.data && response.data.success) {
        toast.success(
          response.data.message || "Ticket status updated successfully"
        );
        setVerificationStatus("success");
      } else {
        toast.error(response.data.message || "Error updating ticket status");
        setVerificationStatus("error");
      }
    } catch (error) {
      console.error("Error updating ticket verification status:", error);
      toast.error("Error updating ticket status. Please try again later.");
      setVerificationStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitTicket = async (values) => {
    setIsLoading(true);
    try {
      // Build payload according to new API structure
      const data = {
        ticket_obj_id: ticketId,
        verification_status: "VERIFIED",
        serial_number: values.serialNumber, // Only for VERIFIED status
        verification_message: values.remark || "", // Remark field maps to verification_message
      };

      // Add confirm flag only when duplicate is found
      if (isDuplicate) {
        data.confirm = true;
      }

      const response = await adminApi.updateTicketVerificationStatus(data);

      if (response.data && response.data.success) {
        toast.success(response.data.message || "Ticket verified successfully");
        setVerificationStatus("success");
      } else {
        toast.error(
          response.data.message || "Error updating ticket verification status"
        );
        setVerificationStatus("error");
      }
    } catch (error) {
      console.error("Error updating ticket verification status:", error);
      toast.error(
        error.response?.data?.message ||
          "Error updating ticket verification status. Please try again later."
      );
      setVerificationStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDuplicate = () => {
    // Just close the dialog - user will select status and submit normally
    setConfirmDialogOpen(false);
    // Keep isDuplicate true so remarks field shows and validation works
  };

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema(),
    validate: validateForm,
    onSubmit: (values) => onSubmit(values),
    enableReinitialize: true,
  });

  // Re-validate when isDuplicate changes
  useEffect(() => {
    if (formik) {
      formik.validateForm();
    }
  }, [isDuplicate]);

  const getStatusIcon = () => {
    switch (formik.values.ticket_status) {
      case "VERIFIED":
        return <CheckCircleIcon className="text-green-500 mr-2" />;
      case "ON_HOLD":
        return <WarningIcon className="text-yellow-500 mr-2" />;
      case "REJECT":
        return <ErrorIcon className="text-red-500 mr-2" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 pb-4">
      <div className="max-w-6xl mx-auto">
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 4,
            bgcolor: "white",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              mb: 4,
              pb: 2,
              borderBottom: "2px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                mb: 1,
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
              }}
            >
              Request Document Verification
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              Verify and process request documents with serial number validation
            </Typography>
          </Box>

          {imageUrls.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  }}
                >
                  Attached Images
                </Typography>
                <Chip
                  label={`${imageUrls.length} ${
                    imageUrls.length === 1 ? "Image" : "Images"
                  }`}
                  color="primary"
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
              <Grid container spacing={3} justifyContent="center">
                {imageUrls.map((url, index) => {
                  // Ensure URL is a string and handle relative URLs
                  const imageUrl =
                    typeof url === "string"
                      ? url
                      : url?.url || url?.image || "";
                  if (!imageUrl) return null;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          overflow: "hidden",
                          boxShadow: 3,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: 6,
                          },
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={imageUrl}
                          alt={`Ticket image ${index + 1}`}
                          sx={{
                            height: { xs: 180, sm: 200, md: 220 },
                            objectFit: "contain",
                            bgcolor: "grey.100",
                          }}
                          onError={(e) => {
                            console.error(
                              `Failed to load image ${index + 1}:`,
                              imageUrl
                            );
                            e.target.style.display = "none";
                          }}
                        />
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          <form onSubmit={formik.handleSubmit}>
            {/* Serial Number Field with Verify Button */}
            {imageUrls.length > 0 && (
              <Box
                sx={{
                  mb: 4,
                  p: { xs: 2, sm: 3 },
                  bgcolor: "grey.50",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                >
                  Serial Number Verification
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      name="serialNumber"
                      value={formik.values.serialNumber}
                      onChange={(e) => {
                        formik.handleChange(e);
                        // Reset verification status when serial number changes
                        if (serialVerificationStatus) {
                          setSerialVerificationStatus(null);
                          setIsDuplicate(false);
                          setIsAutoVerified(false);
                          setDuplicateTickets([]);
                          setSerialMismatchData(null);
                          setApiMessage(null);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.serialNumber &&
                        Boolean(formik.errors.serialNumber)
                      }
                      helperText={
                        formik.touched.serialNumber &&
                        formik.errors.serialNumber
                      }
                      variant="outlined"
                      disabled={isAutoVerified || isVerified}
                      sx={{
                        bgcolor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleVerifySerialNumber}
                              disabled={
                                isVerifyingSerial ||
                                !formik.values.serialNumber.trim() ||
                                isAutoVerified ||
                                isVerified
                              }
                              edge="end"
                              size="small"
                            >
                              {isVerifyingSerial ? (
                                <CircularProgress size={20} />
                              ) : (
                                <SearchIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleVerifySerialNumber}
                      disabled={
                        isVerifyingSerial ||
                        !formik.values.serialNumber.trim() ||
                        isAutoVerified ||
                        isVerified
                      }
                      startIcon={
                        isVerifyingSerial ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <VerifiedUserIcon />
                        )
                      }
                      sx={{
                        height: "56px",
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        boxShadow: 3,
                        "&:hover": {
                          boxShadow: 5,
                        },
                        "&:disabled": {
                          bgcolor: "action.disabledBackground",
                        },
                      }}
                    >
                      {isVerifyingSerial
                        ? "Verifying..."
                        : isAutoVerified
                        ? "Verified ✓"
                        : "Verify Serial"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Verification Status Messages */}
            {serialVerificationStatus === "verified" && isAutoVerified && (
              <Alert
                severity="success"
                sx={{
                  borderRadius: 2,
                  mb: 3,
                  "& .MuiAlert-icon": {
                    fontSize: "1.5rem",
                  },
                }}
                icon={<CheckCircleIcon />}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Serial number verified successfully!
                </Typography>
                <Typography variant="body2">
                  No duplicates found. Ticket will be auto-verified.
                </Typography>
              </Alert>
            )}

            {serialVerificationStatus === "duplicate" && isDuplicate && (
              <Box sx={{ mb: 3 }}>
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 2,
                    mb: 3,
                    "& .MuiAlert-icon": {
                      fontSize: "1.5rem",
                    },
                  }}
                  icon={<WarningIcon />}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Duplicate Serial Number Detected
                  </Typography>
                  <Typography variant="body2">
                    This serial number already exists in our system. Please
                    review the duplicate tickets below.
                  </Typography>
                </Alert>
                {duplicateTickets.length > 0 && (
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
                    >
                      Existing Tickets with this Serial Number:
                    </Typography>
                    <List sx={{ bgcolor: "white", borderRadius: 2 }}>
                      {duplicateTickets.map((ticket, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            "&:last-child": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                Request ID:{" "}
                                {ticket.request_id || ticket.ticket_id || "N/A"}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Status: {ticket.verification_status || "N/A"}
                                </Typography>
                                {ticket.created_at && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Created:{" "}
                                    {new Date(
                                      ticket.created_at
                                    ).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2,
                    "& .MuiAlert-icon": {
                      fontSize: "1.5rem",
                    },
                  }}
                >
                  <Typography variant="body2">
                    You can still submit this serial number, but you will need
                    to provide remarks explaining why you want to overwrite the
                    existing serial number.
                  </Typography>
                </Alert>
              </Box>
            )}

            <Box
              sx={{
                mb: 4,
                p: { xs: 2, sm: 3 },
                bgcolor: "grey.50",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
              >
                Document Verification Status
              </Typography>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="ticket-status-label">
                  Document Verification Status
                </InputLabel>
                <Select
                  labelId="ticket-status-label"
                  id="ticket_status"
                  name="ticket_status"
                  value={formik.values.ticket_status}
                  onChange={(e) => {
                    formik.handleChange(e);
                    // Reset serial verification if switching to REJECT/ON_HOLD
                    if (
                      e.target.value === "ON_HOLD" ||
                      e.target.value === "REJECT"
                    ) {
                      setSerialVerificationStatus(null);
                      setIsDuplicate(false);
                      setIsAutoVerified(false);
                      setDuplicateTickets([]);
                      setSerialMismatchData(null);
                      setApiMessage(null);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  label="Document Verification Status"
                  disabled={isVerified}
                  error={
                    formik.touched.ticket_status &&
                    Boolean(formik.errors.ticket_status)
                  }
                  sx={{
                    bgcolor: "white",
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">
                    <em>Select Status</em>
                  </MenuItem>
                  <MenuItem value="VERIFIED">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckCircleIcon
                        sx={{ color: "success.main", fontSize: 20 }}
                      />
                      Verified
                    </Box>
                  </MenuItem>
                  <MenuItem value="ON_HOLD">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WarningIcon
                        sx={{ color: "warning.main", fontSize: 20 }}
                      />
                      On Hold
                    </Box>
                  </MenuItem>
                  <MenuItem value="REJECT">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ErrorIcon sx={{ color: "error.main", fontSize: 20 }} />
                      Reject
                    </Box>
                  </MenuItem>
                </Select>
                {formik.touched.ticket_status &&
                  formik.errors.ticket_status && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ ml: 2, mt: 0.5 }}
                    >
                      {formik.errors.ticket_status}
                    </Typography>
                  )}
              </FormControl>
            </Box>

            {/* Show remarks field for: REJECT, ON_HOLD, when duplicate is found, or when verified */}
            {(formik.values.ticket_status === "ON_HOLD" ||
              formik.values.ticket_status === "REJECT" ||
              isDuplicate ||
              isVerified) && (
              <Box
                sx={{
                  mb: 4,
                  p: { xs: 2, sm: 3 },
                  bgcolor: "grey.50",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                >
                  Remarks
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Remarks"
                  name="remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.remark && Boolean(formik.errors.remark)}
                  helperText={
                    formik.touched.remark && formik.errors.remark
                      ? formik.errors.remark
                      : isDuplicate
                      ? "Please provide remarks explaining why you want to overwrite the existing serial number"
                      : "Please provide details for this status decision"
                  }
                  variant="outlined"
                  placeholder={
                    isDuplicate
                      ? "Explain why you want to overwrite the existing serial number"
                      : "Please provide details for this status decision"
                  }
                  disabled={isVerified}
                  required
                  sx={{
                    bgcolor: "white",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            )}

            {/* Verification Information Section - Show when verified */}
            {isVerified && ticketVerification && (
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, sm: 4 },
                  mb: 4,
                  // bgcolor: "success.light",
                  borderRadius: 3,
                  border: "1px solid",
                  // borderColor: "success.main",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3,
                    pb: 2,
                    borderBottom: "1px solid",
                    // borderColor: "success.main",
                  }}
                >
                  <VerifiedUserIcon
                    sx={{ fontSize: 32, color: "success.dark", mr: 2 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "success.dark",
                      fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    }}
                  >
                    Verification Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "white",
                        borderRadius: 2,
                        border: "1px solid",
                        // borderColor: "success.main",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Status
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "success.dark", fontWeight: 600, mt: 0.5 }}
                      >
                        {ticketVerification.verification_status}
                      </Typography>
                    </Box>
                  </Grid>
                  {ticketVerification.verified_by && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "white",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "success.main",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 600 }}
                        >
                          Verified By
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 600,
                            mt: 0.5,
                          }}
                        >
                          {ticketVerification.verified_by}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {ticketVerification.verified_at && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "white",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "success.main",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 600 }}
                        >
                          Verified At
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 600,
                            mt: 0.5,
                          }}
                        >
                          {new Date(
                            ticketVerification.verified_at
                          ).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                          })}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {ticketVerification.remarks && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "white",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "success.main",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 600 }}
                        >
                          Remarks
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "text.primary", mt: 0.5 }}
                        >
                          {ticketVerification.remarks}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {ticketVerification.serial_overwrite?.is_overwritten && (
                    <Grid item xs={12}>
                      <Alert
                        severity="warning"
                        sx={{
                          borderRadius: 2,
                          "& .MuiAlert-icon": {
                            fontSize: "1.5rem",
                          },
                        }}
                      >
                        Serial number was overwritten (Duplicate serial number
                        was confirmed)
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Submit Button - Hide when verified */}
            {!isVerified && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 1,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      getStatusIcon()
                    )
                  }
                  sx={{
                    py: 2,
                    px: 6,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 3,
                    boxShadow: 4,
                    minWidth: { xs: "100%", sm: "200px" },
                    "&:hover": {
                      boxShadow: 6,
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {isLoading ? "Processing..." : "Submit Verification"}
                </Button>
              </Box>
            )}
          </form>

          {verificationStatus === "success" && (
            <Alert
              severity="success"
              sx={{
                mt: 4,
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  fontSize: "1.5rem",
                },
              }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Request has been successfully processed!
              </Typography>
            </Alert>
          )}

          {verificationStatus === "error" && (
            <Alert
              severity="error"
              sx={{
                mt: 4,
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  fontSize: "1.5rem",
                },
              }}
              icon={<ErrorIcon />}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                There was an error processing your request. Please try again.
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Confirmation Dialog for Duplicate/Mismatch Serial */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: 8,
            },
          }}
        >
          <DialogTitle
            id="confirm-dialog-title"
            sx={{
              bgcolor:
                serialVerificationStatus === "mismatch"
                  ? "warning.light"
                  : "info.light",
              color: "text.primary",
              fontWeight: 700,
              fontSize: "1.5rem",
              pb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {serialVerificationStatus === "mismatch" ? (
                <WarningIcon sx={{ fontSize: 28 }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 28 }} />
              )}
              {serialVerificationStatus === "mismatch"
                ? "Serial Number Mismatch"
                : "Confirm Serial Number Update"}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box id="confirm-dialog-description">
              {/* Show mismatch information if available */}
              {serialVerificationStatus === "mismatch" &&
                serialMismatchData && (
                  <Box sx={{ mb: 3 }}>
                    <Alert
                      severity="warning"
                      sx={{
                        borderRadius: 2,
                        "& .MuiAlert-icon": {
                          fontSize: "1.5rem",
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 2 }}
                      >
                        Serial Number Mismatch Detected
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2">
                          <strong>Ticket ID:</strong>{" "}
                          {serialMismatchData.ticketId || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Previous Serial Number:</strong>{" "}
                          {serialMismatchData.previous_serial_number || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>New Serial Number:</strong>{" "}
                          {formik.values.serialNumber}
                        </Typography>
                      </Box>
                    </Alert>
                  </Box>
                )}

              {/* Show API message for mismatch case */}
              {serialVerificationStatus === "mismatch" && apiMessage && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-icon": {
                      fontSize: "1.5rem",
                    },
                  }}
                >
                  {apiMessage}
                </Alert>
              )}

              {/* List of duplicate tickets - only show for duplicate case, not mismatch */}
              {serialVerificationStatus === "duplicate" &&
                duplicateTickets.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                    >
                      Existing Tickets with this Serial Number:
                    </Typography>
                    <Paper
                      elevation={1}
                      sx={{
                        bgcolor: "grey.50",
                        borderRadius: 2,
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      <List dense>
                        {duplicateTickets.map((ticket, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              "&:last-child": {
                                borderBottom: "none",
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Request ID:{" "}
                                  {ticket.ticket_id ||
                                    ticket.request_id ||
                                    "N/A"}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Status:{" "}
                                    {ticket.verification_status || "N/A"}
                                  </Typography>
                                  {ticket.created_at && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Created:{" "}
                                      {new Date(
                                        ticket.created_at
                                      ).toLocaleDateString()}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Box>
                )}

              {/* Confirmation message */}
              <Typography
                variant="body1"
                sx={{
                  mt: 3,
                  fontWeight: 500,
                  color: "text.primary",
                  textAlign: "center",
                }}
              >
                Are you sure you want to proceed with this submission?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              p: 3,
              pt: 2,
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={() => {
                setConfirmDialogOpen(false);
                setIsDuplicate(false);
                setSerialVerificationStatus(null);
                setSerialMismatchData(null);
                setDuplicateTickets([]);
                setApiMessage(null);
              }}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDuplicate}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 4,
                fontWeight: 600,
                boxShadow: 3,
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default TicketDocument;
